import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { AuthHeading, AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/Button';
import { FormAlert } from '@/components/ui/Feedback';
import { api, ApiError } from '@/lib/api';
import { NewPasswordFields } from './NewPasswordFields';
import { canSubmitPassword } from './passwordRules';

/**
 * Where an invitation email lands: choose a password and the account opens.
 *
 * Public by necessity — the person cannot sign in yet. The token in the link is
 * the whole credential, so it goes straight to the API and is never shown.
 */
export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const ready = canSubmitPassword(password, confirmation) && token.length > 0;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.post('/invitations/accept', { token, password, password_confirmation: confirmation }, { anonymous: true });
      setDone(true);
      window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That invitation could not be accepted. Ask your site administrator for a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <AuthHeading eyebrow="Account ready" title="You're in" description="Your password is set. Taking you to sign in…" />
        <div className="flex items-center gap-3 rounded-lg border border-signal-200 bg-signal-50 px-4 py-3.5">
          <CheckCircle2 aria-hidden className="h-5 w-5 shrink-0 text-signal" />
          <p className="text-sm font-medium text-signal-700">Account opened</p>
        </div>
        <Link to="/login" className="mt-6 block text-center text-sm font-semibold text-digital transition-colors hover:text-digital-600">
          Continue to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading
        eyebrow="You've been invited"
        title="Open your OneParacon account"
        description="Choose a password. You'll use it with your email address to sign in."
      />

      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {!token && <FormAlert>This link is incomplete. Open it again from the invitation email.</FormAlert>}
        {error && <FormAlert>{error}</FormAlert>}

        <NewPasswordFields password={password} confirmation={confirmation} onPassword={setPassword} onConfirmation={setConfirmation} label="Password" />

        <Button type="submit" size="lg" fullWidth disabled={!ready} loading={submitting}>
          {submitting ? 'Opening…' : 'Open my account'}
        </Button>
      </form>

      <Link to="/login" className="mt-4 block text-center text-sm font-semibold text-digital transition-colors hover:text-digital-600">
        Already set up? Sign in
      </Link>
    </AuthLayout>
  );
}
